import { isAdaptiveHistoryDrift, createAccuracyTierGate, resolveAccuracyM, POOR_ACC_M, HEARTBEAT_MS } from './adaptiveDriftRadius';

/**
 * 轨迹分段：长时间无点、session_id 变化、或不可能的瞬移才切割。
 * 往返尖刺（城区 ↔ 过期网络定位）直接丢掉，避免指挥台画出南北锯齿线。
 * 正在记录的最后一段始终保留，直到最后一个坐标点。
 */
export const TRACK_IDLE_CUT_MS = 30 * 60 * 1000;
/** 尖刺最短边长：小于此距离视为正常折返，不丢点 */
export const TRACK_SPIKE_METERS = 800;
/** 瞬移判定：超过该距离且平均速度过快才切断连线 */
export const TRACK_TELEPORT_MIN_METERS = 600;
/** 约 162 km/h，车辆/搜救可达到；基站跳点通常远快于此 */
export const TRACK_TELEPORT_SPEED_MPS = 45;
/** 精度圈大于该值视为室内/弱星（与 adaptiveDriftRadius.POOR_ACC_M 对齐） */
export const TRACK_POOR_ACCURACY_M = POOR_ACC_M;
/** @deprecated 固定地板已废弃；改用 adaptiveDriftRadius（缺精度默认 25m，步行 5–8m） */
export const TRACK_MIN_MOVE_M = 8;
/** 静止/室内仍可偶尔记一个心跳点，避免轨迹完全空白 */
export const TRACK_DRIFT_HEARTBEAT_MS = HEARTBEAT_MS;
/** 室外移动时最短记点间隔 */
export const TRACK_OUTDOOR_MIN_GAP_MS = 8 * 1000;

function pointTimeMs(point) {
  const raw = point?.recorded_at || point?.timestamp || point?.time || point?.located_at || point?.created_at;
  if (raw == null) return 0;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw > 1e12 ? raw : raw * 1000;
  }
  const t = Date.parse(raw);
  return Number.isNaN(t) ? 0 : t;
}

function pointSessionId(point) {
  return String(point?.track_session_id || point?.session_id || '').trim();
}

function pointWgs(point) {
  if (!point) return null;
  const lng = Number(point.longitude ?? point.lng ?? point.lon ?? point.last_longitude);
  const lat = Number(point.latitude ?? point.lat ?? point.last_latitude);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return { lng, lat };
}

function haversineMeters(lng1, lat1, lng2, lat2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function distPoints(a, b) {
  const pa = pointWgs(a);
  const pb = pointWgs(b);
  if (!pa || !pb) return 0;
  return haversineMeters(pa.lng, pa.lat, pb.lng, pb.lat);
}

function isTeleportLeg(p0, p1) {
  const d = distPoints(p0, p1);
  if (d < TRACK_TELEPORT_MIN_METERS) return false;
  const t0 = pointTimeMs(p0);
  const t1 = pointTimeMs(p1);
  const dt = Math.max(Math.abs(t1 - t0) / 1000, 1);
  return d / dt > TRACK_TELEPORT_SPEED_MPS;
}

function isBounceSpike(prev, mid, next) {
  const a = pointWgs(prev);
  const m = pointWgs(mid);
  const b = pointWgs(next);
  if (!a || !m || !b) return false;
  const dPrev = haversineMeters(a.lng, a.lat, m.lng, m.lat);
  const dNext = haversineMeters(m.lng, m.lat, b.lng, b.lat);
  const chord = haversineMeters(a.lng, a.lat, b.lng, b.lat);
  if (dPrev < TRACK_SPIKE_METERS || dNext < TRACK_SPIKE_METERS) return false;
  if (chord >= Math.min(dPrev, dNext) * 0.45) return false;
  return isTeleportLeg(prev, mid) || isTeleportLeg(mid, next);
}

function sortByTime(points = []) {
  return [...points].sort((a, b) => pointTimeMs(a) - pointTimeMs(b));
}

function pointAccuracy(point) {
  const a = Number(point?.accuracy ?? point?.acc ?? point?.last_accuracy);
  return Number.isFinite(a) && a > 0 ? a : null;
}

/**
 * 是否为精度圈内抖动 / 室内漂移（不应连成轨迹）。
 * 与后端 isHistoryDrift / App shouldRecordTrackPoint 同公式；不得严于入库。
 */
export function isAccuracyDrift(prev, curr, tierGate = null) {
  if (!prev || !curr) return false;
  const d = distPoints(prev, curr);
  const dt = Math.abs(pointTimeMs(curr) - pointTimeMs(prev));
  return isAdaptiveHistoryDrift({
    distM: d,
    accuracyM: pointAccuracy(curr),
    prevAccuracyM: pointAccuracy(prev),
    dtMs: dt,
    tierGate: tierGate || null,
    nowMs: pointTimeMs(curr) || Date.now()
  });
}

export function isSignificantTrackMove(prev, curr) {
  if (!prev) return true;
  return !isAccuracyDrift(prev, curr);
}

/**
 * 压缩室内静止时精度圈内的来回抖动，首末点保留。
 */
export function dropAccuracyDrift(points = []) {
  const list = Array.isArray(points) ? points : [];
  if (list.length < 2) return list;
  const gate = createAccuracyTierGate();
  // Seed applied tier from first point so a later spike does not instantly widen R.
  gate.update(pointTimeMs(list[0]) || Date.now(), resolveAccuracyM(pointAccuracy(list[0])));
  const out = [list[0]];
  for (let i = 1; i < list.length; i += 1) {
    const isLast = i === list.length - 1;
    if (!isLast && isAccuracyDrift(out[out.length - 1], list[i], gate)) continue;
    if (isLast && isAccuracyDrift(out[out.length - 1], list[i], gate)) {
      out[out.length - 1] = list[i];
      continue;
    }
    out.push(list[i]);
  }
  return out;
}

/**
 * 去掉「出城瞬移再瞬移回来」的伪点（GPS ↔ 过期网络定位交错）。
 */
export function dropGpsSpikes(points = []) {
  const list = Array.isArray(points) ? points : [];
  if (list.length < 3) return list;
  const out = [list[0]];
  for (let i = 1; i < list.length - 1; i += 1) {
    const prev = out[out.length - 1];
    const mid = list[i];
    const next = list[i + 1];
    if (isBounceSpike(prev, mid, next)) continue;
    out.push(mid);
  }
  out.push(list[list.length - 1]);
  return out;
}

/** 按时间排序并去掉往返尖刺，供飞视野 / 导出使用 */
export function sanitizeTrackPoints(points = []) {
  const list = Array.isArray(points) ? points : [];
  if (!list.length) return [];
  return dropAccuracyDrift(dropGpsSpikes(sortByTime(list)));
}

/**
 * @param {Array} points 轨迹点（不必预先排序）
 * @param {number} [idleMs]
 * @returns {Array<Array>} 分段后的点数组，含未结束的当前段
 */
export function splitTrackSegments(points = [], idleMs = TRACK_IDLE_CUT_MS) {
  const list = sanitizeTrackPoints(points);
  if (!list.length) return [];
  const segs = [];
  let current = [];
  let lastT = 0;
  let lastSid = '';
  let lastPoint = null;
  list.forEach((p) => {
    const t = pointTimeMs(p);
    const sid = pointSessionId(p);
    const idleCut = current.length > 0 &&
      ((!sid || !lastSid) && lastT > 0 && t > 0 && t - lastT >= idleMs);
    const sessionCut = current.length > 0 && sid && lastSid && sid !== lastSid;
    const teleportCut = current.length > 0 && lastPoint && isTeleportLeg(lastPoint, p);
    if (idleCut || sessionCut || teleportCut) {
      segs.push(current);
      current = [];
    }
    current.push(p);
    lastT = t || lastT;
    lastSid = sid || lastSid;
    lastPoint = p;
  });
  if (current.length) segs.push(current);
  return segs;
}
